import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, updateDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react'; 
import './NotificationManager.css';

const NotificationManager = () => {
    const [target, setTarget] = useState('ALL');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [businesses, setBusinesses] = useState([]);
    const [loadingBusiness, setLoadingBusiness] = useState(false);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [notifyTarget, setNotifyTarget] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchBusinesses(); }, []);

    const fetchBusinesses = async () => {
        setLoadingBusiness(true);
        try {
            const q = query(collection(db, 'users'), where('businessType', '==', 'Business'));
            const snap = await getDocs(q);
            const busData = await Promise.all(snap.docs.map(async (d) => {
                const userData = { id: d.id, ...d.data() };
                
                // 1. Fetch Trusted Count (Emergency accepted)
                const trustedBySnap = await getDocs(query(collection(db, `users/${d.id}/contacts`), 
                    where('emergencyForThem', '==', true), where('forThemStatus', '==', 'ACCEPTED')));
                
                // 2. Fetch Total Contacts Count (Your request)
                const totalContactsSnap = await getDocs(collection(db, `users/${d.id}/contacts`));

                // 3. Fetch Schedule Data (For accessEmail display)
                const schedRef = doc(db, 'users', d.id, 'paymentSchedules', 'current');
                const schedSnap = await getDoc(schedRef);
                const schedData = schedSnap.exists() ? schedSnap.data() : {};

                return { 
                    ...userData, 
                    trustedByCount: trustedBySnap.size,
                    totalContacts: totalContactsSnap.size,
                    accessEmail: schedData.accessEmail || 'Not Set'
                };
            }));
            setBusinesses(busData);
        } catch (e) { console.error("Fetch Error:", e); }
        setLoadingBusiness(false);
    };


    const sendBroadcast = async () => {
        if (!message) return alert("Please enter a message");
        setSending(true);
        try {
            let uids = [];
            // Simplified Target logic to ensure consistency
            if (target === 'SINGLE') {
                const snap = await getDocs(query(collection(db, 'users'), where('email', '==', email.trim())));
                uids = snap.docs.map(d => d.id);
            } else if (target === 'BUSINESS') {
                const snap = await getDocs(query(collection(db, 'users'), where('businessType', '==', 'Business')));
                uids = snap.docs.map(d => d.id);
            } else {
                const snap = await getDocs(collection(db, 'profiles'));
                uids = snap.docs.map(d => d.id);
            }

            const batch = uids.map(uid => 
                addDoc(collection(db, `users/${uid}/in-app-notifications`), {
                    title: "Ccuring Update",
                    message: message,
                    createdAt: serverTimestamp(),
                    read: false,
                    open: "notifications",
                    type: "ADMIN_BROADCAST"
                })
            );
            await Promise.all(batch);
            alert(`Delivered to ${uids.length} accounts.`);
            setMessage('');
        } catch (e) { alert(e.message); }
        setSending(false);
    };

    const toggleDailyCode = async (biz) => {
        const newState = !biz.dailyCodeEnabled;
        if (!window.confirm(`${newState ? 'Enable' : 'Disable'} Daily Code?`)) return;
        try {
            await updateDoc(doc(db, 'users', biz.id), { dailyCodeEnabled: newState });
            fetchBusinesses();
        } catch (e) { alert(e.message); }
    };

    return (
        <div className="notification-container">
            <h1>Ccuring Admin Dashboard</h1>
            
            {/* BROADCAST SECTION (Same as before) */}
            <div className="card broadcast-section">
                <h3>System Broadcast</h3>
                <select value={target} onChange={(e) => setTarget(e.target.value)} className="input-field">
                    <option value="ALL">All Users</option>
                    <option value="BUSINESS">Business Accounts</option>
                    <option value="SINGLE">Individual User</option>
                </select>
                {target === 'SINGLE' && <input type="email" placeholder="Email" className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} />}
                <textarea placeholder="Message..." className="input-field message-area" value={message} onChange={(e) => setMessage(e.target.value)} />
                <button onClick={sendBroadcast} disabled={sending} className="btn btn-primary">{sending ? 'Sending...' : 'Send Broadcast'}</button>
            </div>

            {/* BUSINESS DIRECTORY */}
            <div className="card business-section">
                <div className="header-row">
                    <h3>Business Directory</h3>
                    <input type="text" placeholder="Search businesses..." className="search-bar" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <table className="biz-table">
                    <thead>
                        <tr>
                            <th>Business</th>
                            <th>Admin Access</th>
                            <th>Contacts</th>
                            <th>Trusted</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {businesses.filter(b => (b.name || '').toLowerCase().includes(searchTerm.toLowerCase())).map(biz => (
                            <tr key={biz.id}>
                                <td><strong>{biz.name}</strong><br/><small>{biz.email}</small></td>
                                <td style={{fontSize: '11px', maxWidth: '150px', wordBreak: 'break-all'}}>
                                    <span className="text-purple">{biz.accessEmail}</span>
                                </td>
                                <td><span className="badge badge-gray">{biz.totalContacts}</span></td>
                                <td><span className="badge">{biz.trustedByCount}</span></td>
                                <td>
                                    <div className="action-group">
                                        <button onClick={() => setNotifyTarget(biz)} className="btn btn-primary">Manage/Notify</button>
                                        <button onClick={() => toggleDailyCode(biz)} className={`btn ${biz.dailyCodeEnabled ? 'btn-danger' : 'btn-purple'}`}>{biz.dailyCodeEnabled ? 'OFF' : 'Daily Code'}</button>
                                        <button onClick={() => setSelectedBusiness(biz)} className="btn btn-green">QR</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedBusiness && <QRCodeModal business={selectedBusiness} onClose={() => setSelectedBusiness(null)} />}
            {notifyTarget && <NotifyDialog business={notifyTarget} onClose={() => {setNotifyTarget(null); fetchBusinesses();}} />}
        </div>
    );
};

const QRCodeModal = ({ business, onClose }) => {
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            const snap = await getDocs(query(collection(db, `users/${business.id}/contacts`), where('emergencyForThem', '==', true), where('forThemStatus', '==', 'ACCEPTED')));
            const detailed = await Promise.all(snap.docs.map(async (cDoc) => {
                const p = await getDocs(query(collection(db, 'profiles'), where('uid', '==', cDoc.id)));
                return p.empty ? { id: cDoc.id, name: cDoc.data().name } : { id: cDoc.id, ...p.docs[0].data() };
            }));
            setCustomers(detailed);
            setSelectedIds(detailed.map(u => u.id));
            setLoading(false);
        };
        fetch();
    }, [business]);

    const printSingle = (uid) => {
        const content = document.getElementById(`card-${uid}`).innerHTML;
        const win = window.open('', '', 'height=600,width=800');
        
        win.document.write('<html><head><title>Print Profile</title>');
        // Improved styles for the print window
        win.document.write(`
            <style>
                body { 
                    display: flex; justify-content: center; align-items: center; 
                    height: 100vh; margin: 0; font-family: sans-serif; 
                }
                .qr-card { 
                    text-align: center; border: 2px solid #333; padding: 30px; 
                    width: 3.5in; height: auto; border-radius: 15px;
                }
                img { width: 80px; height: 80px; border-radius: 50%; margin-bottom: 10px; }
                h4 { margin: 10px 0; font-size: 20px; }
                .phone-text { font-weight: bold; color: #555; margin-top: 10px; }
                /* Ensure the SVG scales for printing */
                svg { display: block; margin: 0 auto; }
                .hide-on-print { display: none !important; }
            </style>
        `);
        win.document.write('</head><body><div class="qr-card">');
        win.document.write(content);
        win.document.write('</div></body></html>');
        
        win.document.close();
        
        // Wait for potential images to load before printing
        win.onload = () => {
            win.print();
            win.close();
        };
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header hide-on-print">
                    <h3>{business.name}: QR Directory</h3>
                    <div className="header-actions">
                        <button onClick={() => setSelectedIds(customers.map(u => u.id))} className="btn-small">All</button>
                        <button onClick={() => setSelectedIds([])} className="btn-small">None</button>
                        <button onClick={onClose} className="btn-close">&times;</button>
                    </div>
                </div>
                <div id="printable-area" className="qr-grid">
                    {customers.map(user => {
                        const isSelected = selectedIds.includes(user.id);
                        return (
                            <div key={user.id} id={`card-${user.id}`} className={`qr-card ${!isSelected ? 'hide-on-print' : ''}`}>
                                <div className="card-controls hide-on-print">
                                    <input type="checkbox" checked={isSelected} onChange={() => setSelectedIds(prev => prev.includes(user.id) ? prev.filter(x => x !== user.id) : [...prev, user.id])} />
                                    <button onClick={() => printSingle(user.id)} className="btn-icon">Print</button>
                                </div>
                                {user.photoUrl && <img src={user.photoUrl} crossOrigin="anonymous" alt="" className="profile-img-qr" />}
                                <h4>{user.name}</h4>
                                {/* ✅ FIXED: Using SVG for better print compatibility */}
                                <QRCodeSVG value={user.id} size={150} level="H" includeMargin={true} />
                                <p className="phone-text">{user.phone}</p>
                            </div>
                        );
                    })}
                </div>
                <button onClick={() => setTimeout(() => window.print(), 500)} className="btn btn-green btn-full hide-on-print">Print Selected ({selectedIds.length})</button>
            </div>
        </div>
    );
};
// --- UPDATED COMPONENT: NOTIFY DIALOG WITH MULTI-EMAIL SUPPORT ---
const NotifyDialog = ({ business, onClose }) => {
    const [msg, setMsg] = useState('');
    const [isReminder, setIsReminder] = useState(false);
    const [frequency, setFrequency] = useState('Monthly');
    const [triggerDay, setTriggerDay] = useState('1'); 
    const [triggerTime, setTriggerTime] = useState('09:00');
    const [accessEmail, setAccessEmail] = useState(business.accessEmail === 'Not Set' ? '' : business.accessEmail);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!msg || msg.length > 160) return alert("Message required (1-160 chars)");
        
        setSubmitting(true);
        try {
            // 1. Send immediate notification to contacts
            const contactsSnap = await getDocs(query(collection(db, `users/${business.id}/contacts`), 
                where('emergencyForThem', '==', true), where('forThemStatus', '==', 'ACCEPTED')));
            const uids = contactsSnap.docs.map(d => d.id);

            const batch = uids.map(uid => 
                addDoc(collection(db, `users/${uid}/in-app-notifications`), {
                    title: isReminder ? "Payment Reminder" : `Update: ${business.name}`,
                    message: msg,
                    createdAt: serverTimestamp(),
                    read: false,
                    type: isReminder ? "PAYMENT_REMINDER" : "BUSINESS_ALERT",
                    businessId: business.id
                })
            );

            // 2. Update Schedule & Multiple Access Emails
            const scheduleRef = doc(db, 'users', business.id, 'paymentSchedules', 'current');
            await setDoc(scheduleRef, {
                frequency,
                triggerDay,
                triggerTime,
                message: msg,
                accessEmail: accessEmail.toLowerCase().replace(/\s/g, ''), // Clean spaces
                updatedAt: serverTimestamp(),
                active: true
            }, { merge: true });

            await Promise.all(batch);
            alert("Updated Successfully!");
            onClose();
        } catch (e) { alert(e.message); }
        setSubmitting(false);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content small-modal">
                <h3>Settings for {business.name}</h3>
                <p style={{fontSize: '12px', color: 'gray'}}>Total Contacts: {business.totalContacts}</p>
                
                <textarea maxLength="160" placeholder="Message content..." className="input-field" value={msg} onChange={(e) => setMsg(e.target.value)} />
                
                <div className="checkbox-row">
                    <input type="checkbox" id="reminder" checked={isReminder} onChange={(e) => setIsReminder(e.target.checked)} />
                    <label htmlFor="reminder">Enable Payment Reminder</label>
                </div>

                {isReminder && (
                    <div className="reminder-box">
                        <label>Frequency & Timing</label>
                        <select className="input-field" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                            <option value="Daily">Daily</option><option value="Weekly">Weekly</option><option value="Monthly">Monthly</option>
                        </select>
                        <input type="text" placeholder="Day (1 or Monday)" className="input-field" value={triggerDay} onChange={(e) => setTriggerDay(e.target.value)} />
                        <input type="time" className="input-field" value={triggerTime} onChange={(e) => setTriggerTime(e.target.value)} />
                        
                        <label>Admin Emails (Use comma to add more)</label>
                        <textarea 
                            placeholder="admin1@mail.com, admin2@mail.com" 
                            className="input-field" 
                            style={{height: '60px'}}
                            value={accessEmail} 
                            onChange={(e) => setAccessEmail(e.target.value)} 
                        />
                    </div>
                )}

                <div className="modal-footer">
                    <button onClick={onClose} className="btn btn-danger">Cancel</button>
                    <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary">{submitting ? 'Saving...' : 'Save Settings'}</button>
                </div>
            </div>
        </div>
    );
};

export default NotificationManager;